import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { initialTopUpsDetails, topUpsFormDetailsSchema } from './form-config';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import Jpaper from '@atoms/JPaper';
import { Stack } from '@mui/material';

import { JAutocomplete } from '@components/molucules/JAutocomplete';
import TopUpsFormDetailsData from '@/modules/credits/models/dto/top-ups-form-details-data';
import useTopUpsMutationContext from '@/modules/credits/hooks/useTopUpsMutationContext';
import IOption from '@models/option';
import { useDebouncedCallback } from 'use-debounce';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import JNumberField from '@atoms/JNumberField';
import TopUpsFooter from '../TopUpsFooter';

const TopUpsFormDetails = () => {
  const { t } = useTranslation();
  const { setStepIndex, payload, setPayload } = useTopUpsMutationContext();

  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');

  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );

  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const methods = useForm<TopUpsFormDetailsData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: useMemo(() => initialTopUpsDetails, [initialTopUpsDetails]),
    resolver: yupResolver(topUpsFormDetailsSchema(t)),
  });
  const { handleSubmit, setValue, trigger, formState, getValues } = methods;
  const { isValid, isDirty } = formState;
  const onSubmit = handleSubmit(() => {
    setStepIndex((prev) => (prev ? prev + 1 : 1));
  });

  const debouncedEnterprise = useDebouncedCallback((value) => {
    setEnterpriseInputValue(value);
  }, 1000);

  useEffect(() => {
    if (isDirty) return;
    if (payload?.createCreditTopupDto) {
      setValue('eurosAmount', payload.createCreditTopupDto.eurosAmount);
      setValue('enterpriseId', payload.createCreditTopupDto.enterpriseId);
      setValue('enterpriseName', payload.createCreditTopupDto.enterpriseName);
      setValue('creditsAmount', payload.createCreditTopupDto?.creditsAmount);
      trigger();
    }
  }, [payload, setValue, isDirty]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off" noValidate>
        <Stack gap={2}>
          <Jpaper gap={1} alignItems="stretch">
            <JAutocomplete
              required
              fullWidth
              label={t('NAME_ENTERPRISE')}
              options={enterprisesOptions}
              onChange={(_, newValue) => {
                setValue('enterpriseId', (newValue as IOption)?.value, {
                  shouldDirty: true,
                });
                setValue('enterpriseName', (newValue as IOption)?.label, {
                  shouldDirty: true,
                });
                if (payload) {
                  setPayload({
                    createCreditTopupDto: payload?.createCreditTopupDto,
                  });
                }
                trigger('enterpriseId');
              }}
              onTextChange={(val) => debouncedEnterprise(val)}
              defaultValue={{
                label: payload?.createCreditTopupDto?.enterpriseName ?? '',
                value: payload?.createCreditTopupDto?.enterpriseId ?? '',
              }}
            />
            <JNumberField
              required
              name="creditsAmount"
              fullWidth
              noBorder
              label={t('NUMBER_OF_JOB_COINS')}
              onNumberChange={(newValue) => {
                setValue('creditsAmount', newValue, {
                  shouldDirty: true,
                });
                trigger('creditsAmount');
              }}
              hideButtons={true}
              defaultValue={payload?.createCreditTopupDto?.creditsAmount}
            />
            <JNumberField
              required
              name="amount"
              fullWidth
              noBorder
              label={t('AMOUNT_IN_EUROS')}
              hideButtons={true}
              onNumberChange={(newValue) => {
                setValue('eurosAmount', newValue, {
                  shouldDirty: true,
                });
                trigger('eurosAmount');
              }}
              defaultValue={payload?.createCreditTopupDto?.eurosAmount}
            />
          </Jpaper>
          <TopUpsFooter
            isValid={isValid}
            values={{ createCreditTopupDto: getValues() }}
            submitTab={false}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};

export default TopUpsFormDetails;
